import React, { useRef, useState, useEffect } from "react";

/**
 * Sun & Earth Orbit Lab — interactive two-body gravity simulation.
 *
 * Three sliders (Sun mass, Earth mass, starting distance) reshape the orbit:
 * the default is a clean circle; moving any slider stretches it into an ellipse,
 * and extreme settings break the orbit so the Earth escapes. A heavy Earth makes
 * the Sun visibly wobble around the shared center of mass.
 *
 * The launch speed is fixed from the default config, so every slider genuinely
 * detunes the orbit instead of always re-solving to a circle.
 *
 * Self-contained. Only dependency is React (hooks). Default export.
 * Next.js App Router: add  'use client'  as the first line of the file.
 */

const COL = {
  bg: "#0b1120", stage: "#060a14", panel: "#141f36", panel2: "#1d2b49", line: "#293856",
  ink: "#f4f1e4", dim: "#a9b2c6", gold: "#d9b43c", goldSoft: "#ecd27e",
  sun: "#f0a93b", earth: "#4a9ad0", terra: "#d0724a", ok: "#73b36a",
};

const G = 1200;              // gravitational constant (world units)
const AU = 150;             // world units per "AU" at distance = 1
const V_REF = Math.sqrt(G * 2 / AU); // fixed launch speed from the default config

export default function OrbitLab() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const sim = useRef({
    bodies: null, trail: [], sunTrail: [], running: true, raf: null, last: 0,
    stars: [], view: { cx: 0, cy: 0, scale: 1 }, state: "orbit",
    apoFrame: AU, apoTrue: AU, peri: AU,
  });
  const paramsRef = useRef({ sun: 1, earth: 1, dist: 1 });
  const speedRef = useRef(3.2); // sim-time multiplier, read live by the loop

  const [sunMass, setSunMass] = useState(1);
  const [earthMass, setEarthMass] = useState(1);
  const [dist, setDist] = useState(1);
  const [simSpeed, setSimSpeed] = useState(3.2);
  const [running, setRunning] = useState(true);
  const [narrow, setNarrow] = useState(false);
  const [verdict, setVerdict] = useState({ text: "Stable circular orbit", color: COL.ok });
  const [stat, setStat] = useState("closest 1.00 AU · farthest 1.00 AU");
  const [note, setNote] = useState(
    "A balanced Sun–Earth system: a near-perfect circle. Move any slider and the circle stretches into an ellipse."
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const S = sim.current;
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const W2S = (x, y) => [S.view.cx + x * S.view.scale, S.view.cy + y * S.view.scale];
    const curDist = () => paramsRef.current.dist * AU;
    const sunR = (m) => 10 + 9 * Math.cbrt(m);
    const earthR = (m) => 5 + 5 * Math.cbrt(m);

    function makeStars(w, h) {
      S.stars = [];
      const n = Math.round((w * h) / 7000);
      for (let i = 0; i < n; i++) S.stars.push([Math.random() * w, Math.random() * h, Math.random() * 1.2 + 0.2, Math.random() * 0.5 + 0.15]);
    }
    function fit(w, h) {
      const reach = S.apoFrame + 40;
      S.view.scale = Math.min(w, h) / (reach * 2.3);
      S.view.cx = w * 0.5; S.view.cy = h * 0.5;
    }
    function resize() {
      const r = wrapRef.current.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(10, r.width * dpr);
      canvas.height = Math.max(10, r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      makeStars(r.width, r.height);
      fit(r.width, r.height);
      draw();
    }

    function estimate(Ms, Me, d) {
      const total = Ms + Me, rE = d * Ms / total, rS = d * Me / total;
      const vE = V_REF * Ms / total, vS = V_REF * Me / total;
      let ex = rE, ey = 0, evx = 0, evy = vE, sx = -rS, sy = 0, svx = 0, svy = -vS;
      let minr = d, maxr = d, bound = true;
      const h = 0.02;
      for (let i = 0; i < 9000; i++) {
        const dx = ex - sx, dy = ey - sy, r2 = dx * dx + dy * dy, r = Math.sqrt(r2), f = G / r2;
        evx += -f * Ms * dx / r * h; evy += -f * Ms * dy / r * h; ex += evx * h; ey += evy * h;
        svx += f * Me * dx / r * h; svy += f * Me * dy / r * h; sx += svx * h; sy += svy * h;
        const nr = Math.hypot(ex - sx, ey - sy);
        minr = Math.min(minr, nr); maxr = Math.max(maxr, nr);
        if (nr > d * 7) { bound = false; break; }
      }
      S.peri = minr; S.apoTrue = maxr;
      S.apoFrame = bound ? Math.min(maxr, d * 4.2) : d * 3.2;
      S.state = bound ? ((maxr - minr) / (maxr + minr) < 0.05 ? "circular" : "elliptical") : "escape";
    }

    function launch() {
      const { sun: Ms, earth: Me } = paramsRef.current;
      const d = curDist(), total = Ms + Me;
      const rE = d * Ms / total, rS = d * Me / total;
      const vE = V_REF * Ms / total, vS = V_REF * Me / total;
      S.bodies = {
        sun: { x: -rS, y: 0, vx: 0, vy: -vS, m: Ms, r: sunR(Ms) },
        earth: { x: rE, y: 0, vx: 0, vy: vE, m: Me, r: earthR(Me) },
      };
      S.trail = []; S.sunTrail = [];
      estimate(Ms, Me, d);
      const r = wrapRef.current.getBoundingClientRect();
      fit(r.width, r.height);
      S.running = true;
      updateHUD();
      // (re)start the loop cleanly — cancel any existing frame so a stale handle
      // can never leave the simulation frozen.
      if (S.raf) cancelAnimationFrame(S.raf);
      S.last = performance.now();
      S.raf = requestAnimationFrame(tick);
    }
    S.launch = launch;

    function step(dt) {
      if (!S.bodies) return;
      const sub = 24, h = dt / sub; // more substeps keeps the sped-up orbit accurate
      for (let k = 0; k < sub; k++) {
        const s = S.bodies.sun, e = S.bodies.earth;
        const dx = e.x - s.x, dy = e.y - s.y, r2 = dx * dx + dy * dy, r = Math.sqrt(r2), inv = 1 / r2, ux = dx / r, uy = dy / r;
        const aE = G * s.m * inv, aS = G * e.m * inv;
        e.vx -= aE * ux * h; e.vy -= aE * uy * h; s.vx += aS * ux * h; s.vy += aS * uy * h;
        e.x += e.vx * h; e.y += e.vy * h; s.x += s.vx * h; s.y += s.vy * h;
      }
      S.trail.push([S.bodies.earth.x, S.bodies.earth.y]);
      S.sunTrail.push([S.bodies.sun.x, S.bodies.sun.y]);
      if (S.trail.length > 1400) S.trail.shift();
      if (S.sunTrail.length > 1400) S.sunTrail.shift();
    }

    function updateHUD() {
      const { sun: Ms, earth: Me, dist: d } = paramsRef.current;
      let text, color;
      if (S.state === "escape") { text = "Escapes — not a stable orbit"; color = COL.terra; }
      else if (S.state === "circular") { text = "Stable circular orbit"; color = COL.ok; }
      else { text = "Stable elliptical orbit"; color = COL.gold; }
      setVerdict({ text, color });
      setStat(S.state === "escape" ? "open path — escapes"
        : `closest ${(S.peri / AU).toFixed(2)} AU · farthest ${(S.apoTrue / AU).toFixed(2)} AU`);

      const nearDefault = Math.abs(Ms - 1) < 0.1 && Math.abs(Me - 1) < 0.1 && Math.abs(d - 1) < 0.06;
      let msg;
      if (S.state === "escape")
        msg = "The pull is now too weak to hold the Earth — it swings past once and flies off. This happens when the masses are small or the starting distance is large, so gravity can’t bend the path into a closed loop.";
      else if (nearDefault)
        msg = "A balanced Sun–Earth system: a near-perfect circle. This is the one setting where the speed exactly matches the pull. Move any slider and the circle stretches into an ellipse.";
      else if (S.state === "circular")
        msg = "The speed still matches the pull at this distance, so the orbit stays close to a circle.";
      else if (Me > 1.4)
        msg = "With the Earth this heavy, the Sun visibly wobbles — both bodies swing around their shared center of mass (the gold cross). Neither one truly sits still.";
      else if (Ms >= 2.0 || Me <= 0.3)
        msg = "The Sun now out-pulls the Earth’s motion, reeling it into a tight ellipse — the Earth whips close, then loops back out to where it started.";
      else if (d >= 1.5)
        msg = "Starting farther out, gravity is weaker than the Earth’s speed, so the orbit stretches into a long ellipse — it swings far away before the Sun pulls it back.";
      else if (d <= 0.7)
        msg = "Starting closer in, the Earth is moving too fast for this strong pull, so it flings outward into an ellipse before returning.";
      else
        msg = "The speed no longer matches the pull, so the circle has opened into an ellipse — faster when close to the Sun, slower when far away.";
      setNote(msg);
    }
    S.updateHUD = updateHUD;

    function tick(now) {
      const dt = Math.min(0.03, (now - S.last) / 1000);
      S.last = now;
      if (S.running && S.bodies) {
        step(dt * speedRef.current);
        const b = S.bodies, r = Math.hypot(b.earth.x - b.sun.x, b.earth.y - b.sun.y);
        if (r > curDist() * 7 && S.state !== "escape") { S.state = "escape"; updateHUD(); }
        if (r < b.sun.r * 0.6 + b.earth.r * 0.6) launch(); // avoid integrator blow-up on steep ellipses
      }
      draw();
      S.raf = requestAnimationFrame(tick);
    }

    function draw() {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      if (!S.stars.length) makeStars(w, h);
      ctx.fillStyle = "#fff";
      for (const s of S.stars) { ctx.globalAlpha = s[3]; ctx.beginPath(); ctx.arc(s[0], s[1], s[2], 0, 7); ctx.fill(); }
      ctx.globalAlpha = 1;
      if (!S.bodies) return;

      const [comx, comy] = W2S(0, 0);
      ctx.strokeStyle = "rgba(217,180,60,.35)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(comx - 6, comy); ctx.lineTo(comx + 6, comy); ctx.moveTo(comx, comy - 6); ctx.lineTo(comx, comy + 6); ctx.stroke();

      if (S.trail.length > 1) {
        ctx.lineWidth = 2; ctx.strokeStyle = S.state === "escape" ? COL.terra : COL.gold; ctx.globalAlpha = 0.85;
        ctx.beginPath();
        for (let i = 0; i < S.trail.length; i++) { const [sx, sy] = W2S(S.trail[i][0], S.trail[i][1]); i ? ctx.lineTo(sx, sy) : ctx.moveTo(sx, sy); }
        ctx.stroke(); ctx.globalAlpha = 1;
      }
      if (S.sunTrail.length > 1) {
        ctx.lineWidth = 1.3; ctx.strokeStyle = "rgba(240,169,59,.5)";
        ctx.beginPath();
        for (let i = 0; i < S.sunTrail.length; i++) { const [sx, sy] = W2S(S.sunTrail[i][0], S.sunTrail[i][1]); i ? ctx.lineTo(sx, sy) : ctx.moveTo(sx, sy); }
        ctx.stroke();
      }

      const s = S.bodies.sun, e = S.bodies.earth;
      const [ssx, ssy] = W2S(s.x, s.y), sr = s.r * Math.min(1.4, S.view.scale * 2.2 + 0.4);
      let g = ctx.createRadialGradient(ssx, ssy, sr * 0.3, ssx, ssy, sr * 2.2);
      g.addColorStop(0, "rgba(240,169,59,.55)"); g.addColorStop(1, "rgba(240,169,59,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(ssx, ssy, sr * 2.2, 0, 7); ctx.fill();
      g = ctx.createRadialGradient(ssx - sr * 0.3, ssy - sr * 0.3, sr * 0.2, ssx, ssy, sr);
      g.addColorStop(0, "#ffd27a"); g.addColorStop(1, "#e7922a");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(ssx, ssy, sr, 0, 7); ctx.fill();

      const [esx, esy] = W2S(e.x, e.y), er = e.r * Math.min(1.5, S.view.scale * 2.4 + 0.5);
      g = ctx.createRadialGradient(esx - er * 0.3, esy - er * 0.3, er * 0.2, esx, esy, er);
      g.addColorStop(0, "#8cc4ea"); g.addColorStop(1, "#2d6f9e");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(esx, esy, er, 0, 7); ctx.fill();
    }

    // defer resize into rAF to avoid the "ResizeObserver loop" dev-overlay warning
    let roRaf = null;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 0;
      if (w) setNarrow(window.innerWidth < 780);
      if (roRaf) return;
      roRaf = requestAnimationFrame(() => { roRaf = null; resize(); });
    });
    setNarrow(window.innerWidth < 780);
    ro.observe(wrapRef.current);
    resize();
    launch();
    if (reduceMotion) { S.running = false; setRunning(false); }

    return () => {
      ro.disconnect();
      if (roRaf) cancelAnimationFrame(roRaf);
      if (S.raf) cancelAnimationFrame(S.raf);
      S.raf = null; // reset so a remount (e.g. React StrictMode) restarts the loop
    };
  }, []);

  const onSun = (e) => { const v = parseFloat(e.target.value); paramsRef.current.sun = v; setSunMass(v); relaunch(); };
  const onEarth = (e) => { const v = parseFloat(e.target.value); paramsRef.current.earth = v; setEarthMass(v); relaunch(); };
  const onDist = (e) => { const v = parseFloat(e.target.value); paramsRef.current.dist = v; setDist(v); relaunch(); };
  const onSpeed = (e) => { const v = parseFloat(e.target.value); speedRef.current = v; setSimSpeed(v); }; // no relaunch — just changes pace
  const relaunchTimer = useRef(null);
  function relaunch() {
    clearTimeout(relaunchTimer.current);
    relaunchTimer.current = setTimeout(() => sim.current.launch && sim.current.launch(), 30);
  }
  function launchNow() { sim.current.launch && sim.current.launch(); }
  function togglePause() {
    sim.current.running = !sim.current.running;
    setRunning(sim.current.running);
  }

  const S = styles;
  return (
    <div style={S.root}>
      <style>{`
        .ol-range{-webkit-appearance:none;appearance:none;width:100%;height:6px;border-radius:4px;outline:none;background:#223150}
        .ol-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:20px;height:20px;border-radius:50%;cursor:pointer;border:3px solid ${COL.bg}}
        .ol-range::-moz-range-thumb{width:20px;height:20px;border-radius:50%;cursor:pointer;border:3px solid ${COL.bg}}
        .ol-range:focus-visible{box-shadow:0 0 0 3px rgba(217,180,60,.35)}
        .ol-sun::-webkit-slider-thumb{background:${COL.sun}} .ol-sun::-moz-range-thumb{background:${COL.sun}}
        .ol-earth::-webkit-slider-thumb{background:${COL.earth}} .ol-earth::-moz-range-thumb{background:${COL.earth}}
        .ol-dist::-webkit-slider-thumb{background:${COL.gold}} .ol-dist::-moz-range-thumb{background:${COL.gold}}
        .ol-speed::-webkit-slider-thumb{background:${COL.dim}} .ol-speed::-moz-range-thumb{background:${COL.dim}}
        .ol-btn:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(217,180,60,.4)}
        .ol-btn:active{transform:translateY(1px)}
      `}</style>

      <header>
        <div style={S.eyebrow}>Lesson 18 · Universal Gravitation</div>
        <div style={S.h1}>Sun &amp; Earth Orbit Lab <span style={S.h1em}>— change the masses and distance, watch the path change</span></div>
      </header>

      <div style={{ ...S.stageRow, gridTemplateColumns: narrow ? "1fr" : "minmax(0,1fr) 330px" }}>
        <div ref={wrapRef} style={S.stage}>
          <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
          <div style={S.hud}>
            <div style={S.chip}><span style={{ ...S.dot, background: verdict.color }} />{verdict.text}</div>
            <div style={S.statBox}>{stat}</div>
          </div>
        </div>

        <div style={S.rail}>
          <Ctl label="Sun mass" color={COL.sun} cls="ol-sun" min="0.3" max="4" step="0.05" value={sunMass} onChange={onSun} unit="×" />
          <Ctl label="Earth mass" color={COL.earth} cls="ol-earth" min="0.05" max="3" step="0.05" value={earthMass} onChange={onEarth} unit="×" />
          <Ctl label="Starting distance" color={COL.gold} cls="ol-dist" min="0.4" max="2.4" step="0.02" value={dist} onChange={onDist} unit="AU" digits={2} />

          <div style={S.divider} />
          <Ctl label="Simulation speed" color={COL.dim} cls="ol-speed" min="0.3" max="8" step="0.1" value={simSpeed} onChange={onSpeed} unit="×" digits={1} />

          <div style={S.btns}>
            <button className="ol-btn" style={{ ...S.btn, ...S.btnPrimary }} onClick={launchNow}>↻ Launch orbit</button>
            <button className="ol-btn" style={S.btn} onClick={togglePause}>{running ? "Pause" : "Resume"}</button>
          </div>

          <div style={S.legend}>
            <span style={S.legItem}><i style={{ ...S.legI, background: COL.sun }} />Sun</span>
            <span style={S.legItem}><i style={{ ...S.legI, background: COL.earth }} />Earth</span>
            <span style={S.legItem}><i style={{ ...S.legI, background: COL.gold }} />path</span>
          </div>

          <div style={S.note}>{note}</div>
        </div>
      </div>

      <footer style={S.footer}>
        A two-body gravity simulation. Masses and distances are scaled for the screen; the behavior — bound orbits, wobble, and escape — follows Newton’s law of gravitation.
      </footer>
    </div>
  );
}

function Ctl({ label, color, cls, min, max, step, value, onChange, unit, digits = 2 }) {
  return (
    <div style={styles.ctl}>
      <div style={styles.ctlLab}>
        <span style={{ ...styles.ctlName, color }}>{label}</span>
        <span style={styles.ctlVal}>{value.toFixed(digits)} <small style={{ fontSize: 11, color: COL.dim }}>{unit}</small></span>
      </div>
      <input className={`ol-range ${cls}`} type="range" min={min} max={max} step={step} value={value} onChange={onChange} />
    </div>
  );
}

const styles = {
  root: { background: COL.bg, color: COL.ink, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 12, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", maxWidth: 1200, margin: "0 auto", boxSizing: "border-box" },
  eyebrow: { fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: COL.gold, fontWeight: 700 },
  h1: { fontFamily: "Georgia,serif", fontWeight: 700, fontSize: "clamp(20px,3vw,28px)", marginTop: 2 },
  h1em: { color: COL.goldSoft, fontWeight: 400 },
  stageRow: { display: "grid", gridTemplateColumns: "minmax(0,1fr) 330px", gap: 14 },
  stage: { position: "relative", background: COL.stage, border: `1px solid ${COL.line}`, borderRadius: 14, overflow: "hidden", minHeight: 360, minWidth: 0 },
  hud: { position: "absolute", left: 14, top: 14, right: 14, display: "flex", gap: 8, flexWrap: "wrap", pointerEvents: "none" },
  chip: { fontSize: 12.5, fontWeight: 700, padding: "6px 12px", borderRadius: 999, background: "rgba(20,31,54,.85)", border: `1px solid ${COL.line}`, boxShadow: "0 2px 10px rgba(0,0,0,.4)" },
  dot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 7, verticalAlign: "middle" },
  statBox: { fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 11.5, color: COL.dim, background: "rgba(6,10,20,.8)", border: `1px solid ${COL.line}`, borderRadius: 8, padding: "5px 10px", fontVariantNumeric: "tabular-nums" },
  rail: { background: COL.panel, border: `1px solid ${COL.line}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
  divider: { height: 1, background: COL.line, margin: "2px 0" },
  ctl: { display: "flex", flexDirection: "column", gap: 7 },
  ctlLab: { display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  ctlName: { fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 },
  ctlVal: { fontFamily: "Georgia,serif", fontSize: 17, fontVariantNumeric: "tabular-nums" },
  btns: { display: "flex", gap: 8, flexWrap: "wrap" },
  btn: { fontSize: 13, fontWeight: 600, padding: "9px 14px", borderRadius: 8, cursor: "pointer", border: `1px solid ${COL.line}`, background: COL.panel2, color: COL.ink, fontFamily: "inherit" },
  btnPrimary: { background: COL.gold, color: "#241a00", borderColor: COL.gold },
  legend: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: COL.dim },
  legItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  legI: { width: 11, height: 11, borderRadius: "50%" },
  note: { fontFamily: "Georgia,serif", fontSize: 13.5, lineHeight: 1.5, color: COL.dim, borderTop: `1px solid ${COL.line}`, paddingTop: 12 },
  footer: { fontSize: 11, color: COL.dim, textAlign: "center", opacity: 0.72 },
};